import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './UI';
import { Zap, Crown, X, Sparkles, Loader2, Clock } from 'lucide-react';
import { createCheckout } from '../api';

export function ProBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${className}`}
      style={{
        background: 'linear-gradient(90deg,#f59e0b,#f97316)',
        color: '#ffffff',
      }}
    >
      <Crown size={9} /> PRO
    </span>
  );
}

export function TrialBadge({ daysRemaining }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
      style={{ background: '#dbeafe', color: '#1e40af' }}
    >
      <Clock size={9} /> TRIAL · {daysRemaining}d
    </span>
  );
}

export function UpgradeInline({ title, description, onDismiss }) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-start gap-3 p-3.5 rounded-xl"
      style={{
        background: 'linear-gradient(90deg,#eff6ff 0%,#fef3c7 100%)',
        border: '1px solid #fde68a',
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: '#ffffff' }}
      >
        <Sparkles size={16} style={{ color: '#f59e0b' }} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          <b className="text-sm">{title}</b>
          <ProBadge />
        </div>
        <div className="text-xs mt-0.5" style={{ color: '#78350f' }}>
          {description}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => navigate('/upgrade')}
          className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg"
          style={{ background: 'var(--lf-navy)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          <Zap size={12} /> Upgrade
        </button>
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#78350f' }}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export function UpgradeModal({ open, onClose, feature }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const trialExpired = user?.trial?.expired;

  const handleUpgrade = async () => {
    setBusy(true);
    try {
      const res = await createCheckout(window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      navigate('/upgrade');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md p-6"
        style={{ boxShadow: '0 20px 50px rgba(15,23,42,.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Crown size={18} style={{ color: '#f59e0b' }} />
            <b className="text-base">
              {trialExpired ? 'Your trial has ended' : 'Pro feature'}
            </b>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>
        <h2 className="text-xl font-bold mb-1">{feature || 'Upgrade to Lindore Pro'}</h2>
        <p className="text-sm mb-4" style={{ color: 'var(--lf-muted)' }}>
          {trialExpired
            ? 'Your 7-day Pro trial has ended. Subscribe for $49/mo to keep unlimited AI explanations, rate chat, load-board matches and PDF rate confirmations.'
            : 'This feature is included with Pro ($49/mo). Unlock GPT-5 explanations, rate negotiation, load-board matches and PDF rate confirmations.'}
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Not now
          </Button>
          <Button variant="primary" onClick={handleUpgrade} disabled={busy}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </div>
  );
}

export function useUpgradeGate() {
  const { user } = useAuth();
  const [modal, setModal] = useState({ open: false, feature: '' });
  const guard = (feature) => {
    if (user?.effective_pro || user?.is_pro) return true;
    setModal({ open: true, feature });
    return false;
  };
  const Modal = (
    <UpgradeModal
      open={modal.open}
      feature={modal.feature}
      onClose={() => setModal({ open: false, feature: '' })}
    />
  );
  return { guard, Modal };
}
